import { format } from "date-fns";
import { DateTime } from "luxon";

export interface TimeOption {
  value: string; // 24-hour format (e.g., "09:00", "13:30")
  label: string; // 12-hour format (e.g., "9:00 AM", "1:30 PM")
}

/**
 * Generate an array of 30-minute time intervals with both 24-hour values and 12-hour labels.
 * Used for time selection dropdowns in forms.
 *
 * @returns Array of TimeOption objects with value (24h) and label (12h) properties
 */
export function generateTimeOptions(): TimeOption[] {
  const options: TimeOption[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const value = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      const displayTime = format(new Date(`2000-01-01T${value}`), "h:mm aa");

      options.push({
        value,
        label: displayTime,
      });
    }
  }

  return options;
}

/**
 * Generate an array of 30-minute increments in a 12-hour format.
 * This function matches the DatePicker component's generateHalfHourIncrements12h function.
 *
 * @returns Array of time strings in 12-hour format (e.g., ["12:00 AM", "12:30 AM", ...])
 */
export function generateHalfHourIncrements12h(): string[] {
  const times: string[] = [];

  for (let hour = 0; hour < 24; hour++) {
    const d1 = new Date(2000, 0, 1, hour, 0);
    const d2 = new Date(2000, 0, 1, hour, 30);

    times.push(format(d1, "h:mm aa"));
    times.push(format(d2, "h:mm aa"));
  }

  return times;
}

/**
 * Convert a UTC datetime string to display format in a specific timezone.
 * Uses the same logic as DatePicker component for consistency.
 *
 * @param utcDateString - ISO string of the UTC date
 * @param timeZone - Target timezone identifier (e.g., "America/New_York")
 * @returns Object with date, startTime, and endTime for display
 */
export function convertUtcToTimezone(
  utcDateString: string,
  timeZone: string,
): { date: string; time: string } {
  const dateTime = DateTime.fromISO(utcDateString).setZone(timeZone);

  return {
    date: dateTime.toFormat("yyyy-MM-dd"),
    time: dateTime.toFormat("HH:mm"),
  };
}

/**
 * Convert timezone-aware time back to UTC using the same logic as DatePicker.
 * This ensures consistency with how session start/end dates are handled.
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:mm format
 * @param timeZone - Source timezone identifier
 * @returns UTC Date object
 */
export function convertTimezoneToUtc(
  date: string,
  time: string,
  timeZone: string,
): Date {
  const [hours, minutes] = time.split(":").map(Number);

  let dt = DateTime.fromFormat(date, "yyyy-MM-dd").setZone(timeZone);
  dt = dt.set({ hour: hours, minute: minutes });

  return dt.toUTC().toJSDate();
}
